/**
 * PayoffChart — профиль доходности (дизайн «Aikido Invest», ТЗ §3).
 *
 * Презентационный слой: данные приходят с бэка (/portfolios/{id}/payoff),
 * формулы/математика не меняются. Реколор + раскладка под прототип
 * docs/options-analyzer-prototype.html:
 *   • синяя кинкнутая линия — профиль НА ЭКСПИРАЦИИ (всегда видна);
 *   • розовая (magenta) — временна́я стоимость на выбранную дату (по умолчанию);
 *   • Call зелёная / Put красная / Депозит синий-пунктир — по кнопкам (депозит скрыт по умолч.);
 *   • ось X — окно 75–250% спота; кружки в точках нуля (BE); легенда справа.
 *
 * Это «профиль доходности», НЕ «график цены».
 */

import { useEffect, useMemo, useState, useRef } from "react";
import type { PayoffResponse } from "../api";
import { priceLegSum, zeroCrossings, hasCushion } from "../lib/breakeven";

interface Props {
  payoff: PayoffResponse;
  spot: number;
  dateIndex: number;
  onDateIndexChange: (i: number) => void;
}

// Цвет ноги: опционы по типу (Call/Put из имени), консерватив — синий «депозит».
function legColor(meta: { name: string; type: string }): string {
  if (meta.type !== "option") return "var(--depo)";
  return /put|пут/i.test(meta.name) ? "var(--put)" : "var(--call)";
}
function legDashed(meta: { type: string }): boolean {
  return meta.type !== "option";
}

export function PayoffChart({ payoff, spot, dateIndex, onDateIndexChange }: Props) {
  const [showTval, setShowTval] = useState(true);   // розовая — временна́я стоимость (now)
  const [showExpiry, setShowExpiry] = useState(true); // синяя — экспирация (always default)
  const [withDeposit, setWithDeposit] = useState(false); // пресет: профиль с консервативным доходом
  const [enabledLegs, setEnabledLegs] = useState<Set<string>>(new Set());
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const cushion = hasCushion(payoff);
  const expiryIndex = payoff.dates.length - 1;
  const isAtExpiry = dateIndex === expiryIndex;
  const xs = payoff.prices;
  // Пресет «Без депозита» (по умолч.): только «ценовые» ноги (опционы/фьючерсы/акции) — чистый профиль премий.
  // Пресет «С депозитом»: полная сумма портфеля (payoff.pnl уже включает подушку) — кривая поднята на доход депозита,
  // точки нуля сдвигаются внутрь (зона убытка покрывается). Отдельная линия депозита остаётся в любом случае.
  const ysNow = useMemo(() => withDeposit ? (payoff.pnl[dateIndex] ?? []) : priceLegSum(payoff, dateIndex), [payoff, dateIndex, withDeposit]);
  const ysExp = useMemo(() => withDeposit ? (payoff.pnl[expiryIndex] ?? []) : priceLegSum(payoff, expiryIndex), [payoff, expiryIndex, withDeposit]);
  // Точки нуля — по выбранному профилю на экспирации (без депозита = премиальные BE; с депозитом = реальные no-loss).
  const beAll = useMemo(() => zeroCrossings(xs, ysExp), [xs, ysExp]);

  // Стартовое окно по ЦЕНТРУ спота (~±20%, т.е. до 120%); зум: внутрь — уже, наружу — до 50–250% данных.
  // Симметрично вокруг спота → спот посередине. Если точки нуля шире ±20% — расширяем симметрично, чтобы их было видно.
  const dataLo = xs[0], dataHi = xs[xs.length - 1];
  const bes = beAll.length ? beAll : [spot];
  const beHalf = Math.max(...bes.map((b) => Math.abs(b - spot))) * 1.12;
  const half = Math.max(spot * 0.2, beHalf);
  const defaultLo = Math.max(dataLo, spot - half);
  const defaultHi = Math.min(dataHi, spot + half);
  const fullRange: [number, number] = [defaultLo, defaultHi];
  const [xRange, setXRange] = useState<[number, number]>(fullRange);
  useEffect(() => { setXRange([defaultLo, defaultHi]); /* eslint-disable-next-line */ }, [dataLo, dataHi, spot]);

  function zoom(factor: number) {
    const [lo, hi] = xRange;
    const center = (lo + hi) / 2;
    const w = (hi - lo) * factor;
    let nlo = center - w / 2, nhi = center + w / 2;
    if (nlo < dataLo) { nhi += dataLo - nlo; nlo = dataLo; }
    if (nhi > dataHi) { nlo -= nhi - dataHi; nhi = dataHi; }
    nlo = Math.max(nlo, dataLo); nhi = Math.min(nhi, dataHi);
    if (nhi - nlo < (dataHi - dataLo) * 0.02) return;
    setXRange([nlo, nhi]);
  }
  function resetZoom() { setXRange([defaultLo, defaultHi]); }

  // SVG
  const W = 820, H = 360;
  const P = { l: 64, r: 18, t: 22, b: 44 };
  const innerW = W - P.l - P.r, innerH = H - P.t - P.b;
  const [x0, xN] = xRange;

  const visibleIdx = useMemo(
    () => xs.map((x, i) => ({ x, i })).filter((p) => p.x >= x0 && p.x <= xN).map((p) => p.i),
    [xs, x0, xN]
  );
  const pickVisible = (arr: number[]) => visibleIdx.map((i) => arr[i]);

  const allY: number[] = [];
  if (showTval) allY.push(...pickVisible(ysNow));
  if (showExpiry) allY.push(...pickVisible(ysExp));
  for (const meta of payoff.legs_meta)
    if (enabledLegs.has(meta.id)) allY.push(...pickVisible(payoff.pnl_per_leg[meta.id]?.[dateIndex] ?? []));
  if (!allY.length) allY.push(0);
  const minY = Math.min(...allY, 0), maxY = Math.max(...allY, 0);
  const padY = Math.max((maxY - minY) * 0.08, 100);
  const y0 = minY - padY, y1 = maxY + padY;

  const xToPx = (x: number) => P.l + ((x - x0) / (xN - x0)) * innerW;
  const yToPx = (y: number) => P.t + (1 - (y - y0) / (y1 - y0)) * innerH;
  const pxToX = (px: number) => x0 + ((px - P.l) / innerW) * (xN - x0);

  function pathFor(ys: number[]): string {
    const start = Math.max(0, (visibleIdx[0] ?? 0) - 1);
    const end = Math.min(xs.length - 1, (visibleIdx[visibleIdx.length - 1] ?? xs.length - 1) + 1);
    const segs: string[] = [];
    for (let i = start; i <= end; i++)
      segs.push(`${i === start ? "M" : "L"}${xToPx(xs[i]).toFixed(1)},${yToPx(ys[i]).toFixed(1)}`);
    return segs.join(" ");
  }

  function niceTicks(lo: number, hi: number, n: number): number[] {
    const range = hi - lo, step0 = range / n;
    const mag = Math.pow(10, Math.floor(Math.log10(step0)));
    const norm = step0 / mag;
    const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
    const first = Math.ceil(lo / step) * step;
    const t: number[] = [];
    for (let v = first; v <= hi + 1e-9; v += step) t.push(Math.round(v / step) * step);
    return t;
  }
  const yTicks = useMemo(() => niceTicks(y0, y1, 6), [y0, y1]);
  const xTicks = useMemo(() => niceTicks(x0, xN, 9), [x0, xN]);

  const currentDate = payoff.dates[dateIndex];
  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" });
  const fmtMoney = (v: number) => v.toLocaleString("ru-RU", { maximumFractionDigits: 0 });

  function jumpDays(days: number) {
    if (payoff.dates.length < 2) return;
    const first = new Date(payoff.dates[0]).getTime();
    const target = first + days * 86400_000;
    let best = 0, bd = Infinity;
    payoff.dates.forEach((d, i) => { const dt = Math.abs(new Date(d).getTime() - target); if (dt < bd) { bd = dt; best = i; } });
    onDateIndexChange(best);
  }
  function toggleLeg(id: string) {
    const s = new Set(enabledLegs); s.has(id) ? s.delete(id) : s.add(id); setEnabledLegs(s);
  }
  function svgPx(e: { clientX: number; clientY: number }) {
    if (!svgRef.current) return null;
    const r = svgRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (W / r.width), y: (e.clientY - r.top) * (H / r.height) };
  }
  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const pt = svgPx(e); if (!pt) return;
    if (pt.x < P.l || pt.x > W - P.r) { setHoverIdx(null); return; }
    const sx = pxToX(pt.x); let bi = 0, bd = Infinity;
    xs.forEach((s, i) => { const d = Math.abs(s - sx); if (d < bd) { bd = d; bi = i; } });
    setHoverIdx(bi);
  }

  // Точки нуля (BE) — кружки на синей линии экспирации
  const beMarkers = beAll.filter((b) => b >= x0 && b <= xN);

  return (
    <div>
      <div className="pf-head">
        <div className="datebig">Профиль доходности <span style={{ color: "var(--txt-3)", fontWeight: 400, fontSize: 11 }}>на {fmtDate(currentDate)}</span></div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {cushion && (
            <div className="seg" role="tablist" title="«С депозитом» поднимает кривую на доход подушки — точки нуля сдвигаются внутрь (зона убытка покрывается). «Без депозита» — чистый профиль премий.">
              <button className={!withDeposit ? "on" : ""} onClick={() => setWithDeposit(false)}>Без депозита</button>
              <button className={withDeposit ? "on" : ""} onClick={() => setWithDeposit(true)}>С депозитом</button>
            </div>
          )}
          <div className="zoom" style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button onClick={() => zoom(1.4)} title="Отдалить">−</button>
            <button onClick={() => zoom(0.7)} title="Приблизить">+</button>
            <button onClick={resetZoom} title="Сбросить масштаб">⟲</button>
          </div>
        </div>
      </div>
      {cushion && (
        <div className="hint" style={{ marginTop: 6 }}>
          {withDeposit
            ? "Профиль с консервативным доходом: кривая поднята на доход подушки, точки нуля — реальные «без зон убытка»."
            : "Профиль премий (без депозита). Депозит влияет только на стоимостную часть; включи «С депозитом», чтобы увидеть его вклад."}
        </div>
      )}

      <div className="plotwrap" style={{ marginTop: 10 }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="a-chart"
          onMouseMove={onMove} onMouseLeave={() => setHoverIdx(null)}>
          {/* сетка */}
          {yTicks.map((t) => <line key={`yg${t}`} x1={P.l} x2={W - P.r} y1={yToPx(t)} y2={yToPx(t)} stroke="var(--gridc)" strokeWidth={1} opacity={0.4} />)}
          {xTicks.map((t) => <line key={`xg${t}`} x1={xToPx(t)} x2={xToPx(t)} y1={P.t} y2={H - P.b} stroke="var(--gridc)" strokeWidth={1} opacity={0.4} />)}

          {/* зоны: между BE — убыток (розовая заливка), вне — прибыль */}
          {(() => {
            const z = beAll.filter((b) => b >= x0 && b <= xN).sort((a, b) => a - b);
            if (z.length < 2) return null;
            const l = xToPx(z[0]), r = xToPx(z[z.length - 1]);
            return <rect x={l} y={P.t} width={Math.max(0, r - l)} height={innerH} fill="var(--tval)" opacity={0.06} />;
          })()}

          {/* Y=0 */}
          <line x1={P.l} x2={W - P.r} y1={yToPx(0)} y2={yToPx(0)} stroke="var(--axisc)" strokeWidth={1} />

          {/* spot */}
          {spot >= x0 && spot <= xN && (
            <>
              <line x1={xToPx(spot)} x2={xToPx(spot)} y1={P.t} y2={H - P.b} stroke="var(--axisc)" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
              <text x={xToPx(spot)} y={P.t - 6} textAnchor="middle" fontSize={11} fill="var(--txt-2)">Spot {spot.toFixed(2)}</text>
            </>
          )}

          {/* per-leg */}
          {payoff.legs_meta.map((meta) => enabledLegs.has(meta.id) && (
            <path key={meta.id} d={pathFor(payoff.pnl_per_leg[meta.id]?.[dateIndex] ?? [])} fill="none"
              stroke={legColor(meta)} strokeWidth={1.6} strokeDasharray={legDashed(meta) ? "5 4" : undefined} opacity={0.9} />
          ))}

          {/* экспирация — синяя кинкнутая, всегда */}
          {showExpiry && <path d={pathFor(ysExp)} fill="none" stroke="var(--exp)" strokeWidth={2.4} />}

          {/* временна́я стоимость — розовая (now) */}
          {showTval && !isAtExpiry && <path d={pathFor(ysNow)} fill="none" stroke="var(--tval)" strokeWidth={2.4} />}

          {/* BE кружки на линии экспирации */}
          {beMarkers.map((be, i) => {
            // y на линии экспирации ≈ 0 в точке BE
            return (
              <g key={`be${i}`}>
                <circle cx={xToPx(be)} cy={yToPx(0)} r={4} fill="var(--plot)" stroke="var(--exp)" strokeWidth={2} />
                <text x={xToPx(be)} y={H - P.b + 30} textAnchor="middle" fontSize={10} fill="var(--exp)">BE {be.toFixed(0)}</text>
              </g>
            );
          })}

          {/* hover */}
          {hoverIdx !== null && (
            <g>
              <line x1={xToPx(xs[hoverIdx])} x2={xToPx(xs[hoverIdx])} y1={P.t} y2={H - P.b} stroke="var(--axisc)" strokeWidth={0.6} strokeDasharray="2 2" />
              {showTval && !isAtExpiry && <circle cx={xToPx(xs[hoverIdx])} cy={yToPx(ysNow[hoverIdx])} r={3} fill="var(--tval)" />}
              {showExpiry && <circle cx={xToPx(xs[hoverIdx])} cy={yToPx(ysExp[hoverIdx])} r={3} fill="var(--exp)" />}
              {(() => {
                const tx = xToPx(xs[hoverIdx]) + 12;
                const tox = tx > W - 170 ? xToPx(xs[hoverIdx]) - 150 : tx;
                return (
                  <g transform={`translate(${tox}, ${P.t + 6})`}>
                    <rect x={0} y={0} width={140} height={isAtExpiry ? 34 : 50} fill="var(--surface)" stroke="var(--line)" rx={6} />
                    <text x={8} y={15} fontSize={11} fill="var(--txt-2)">Цена: {xs[hoverIdx].toFixed(2)}</text>
                    {showTval && !isAtExpiry && <text x={8} y={30} fontSize={11} fill="var(--tval)" fontWeight={600}>Сейчас: {fmtMoney(ysNow[hoverIdx])}</text>}
                    <text x={8} y={isAtExpiry ? 28 : 45} fontSize={11} fill="var(--exp)">Эксп.: {fmtMoney(ysExp[hoverIdx])}</text>
                  </g>
                );
              })()}
            </g>
          )}

          {/* оси */}
          {yTicks.map((t) => <text key={`yl${t}`} x={P.l - 6} y={yToPx(t) + 4} textAnchor="end" fontSize={10.5} fill="var(--txt-3)">{fmtMoney(t)}</text>)}
          {xTicks.map((t) => <text key={`xl${t}`} x={xToPx(t)} y={H - P.b + 15} textAnchor="middle" fontSize={10.5} fill="var(--txt-3)">{t.toFixed(0)}</text>)}
          <text x={P.l + innerW / 2} y={H - 4} textAnchor="middle" fontSize={11} fill="var(--txt-2)">Цена актива</text>
          <text x={13} y={P.t + innerH / 2} textAnchor="middle" fontSize={11} fill="var(--txt-2)" transform={`rotate(-90 13 ${P.t + innerH / 2})`}>Прибыль / убыток, $</text>
        </svg>
      </div>

      {/* легенда */}
      <div className="scn">
        <button className={showExpiry ? "on" : ""} onClick={() => setShowExpiry(!showExpiry)} title="Профиль на дату экспирации — кинкнутая «галка» (внутренняя стоимость).">
          <span className="sw" style={{ background: "var(--exp)" }} /> На экспирации
        </button>
        <button className={showTval ? "on" : ""} onClick={() => setShowTval(!showTval)} disabled={isAtExpiry} title="Временна́я стоимость на выбранную дату (BS-кривая).">
          <span className="sw" style={{ background: "var(--tval)" }} /> Сейчас / врем. стоимость
        </button>
        {payoff.legs_meta.map((meta) => (
          <button key={meta.id} className={enabledLegs.has(meta.id) ? "on" : ""} onClick={() => toggleLeg(meta.id)}>
            <span className={`sw ${legDashed(meta) ? "dash" : ""}`} style={{ background: legColor(meta), color: legColor(meta) }} /> {meta.name}
          </button>
        ))}
      </div>

      {/* таймлайн */}
      {payoff.dates.length > 1 && (
        <div className="timeline">
          <div className="tl-btns">
            <button className={dateIndex === 0 ? "on" : ""} onClick={() => onDateIndexChange(0)}>Сегодня</button>
            <button onClick={() => jumpDays(90)}>+3 мес</button>
            <button onClick={() => jumpDays(180)}>+6 мес</button>
            <button onClick={() => jumpDays(270)}>+9 мес</button>
            <button className={isAtExpiry ? "on" : ""} onClick={() => onDateIndexChange(expiryIndex)}>Экспирация</button>
          </div>
          <input type="range" min={0} max={payoff.dates.length - 1} value={dateIndex} onChange={(e) => onDateIndexChange(+e.target.value)} />
        </div>
      )}

      <Breakdown payoff={payoff} dateIndex={dateIndex} spot={spot} />
    </div>
  );
}

function Breakdown({ payoff, dateIndex, spot }: { payoff: PayoffResponse; dateIndex: number; spot: number }) {
  if (payoff.dates.length < 2 || payoff.legs_meta.length === 0) return null;
  const spotIdx = payoff.prices.reduce((best, p, i) => Math.abs(p - spot) < Math.abs(payoff.prices[best] - spot) ? i : best, 0);
  const expIdx = payoff.dates.length - 1;
  const rows = payoff.legs_meta.map((meta) => {
    const m = payoff.pnl_per_leg[meta.id]; if (!m) return null;
    return { name: meta.name, type: meta.type, now: m[0]?.[spotIdx] ?? 0, at: m[dateIndex]?.[spotIdx] ?? 0, exp: m[expIdx]?.[spotIdx] ?? 0 };
  }).filter(Boolean) as { name: string; type: string; now: number; at: number; exp: number }[];
  if (!rows.length) return null;
  const tN = rows.reduce((s, r) => s + r.now, 0), tA = rows.reduce((s, r) => s + r.at, 0), tE = rows.reduce((s, r) => s + r.exp, 0);
  const fmt = (v: number) => v.toLocaleString("ru-RU", { signDisplay: "always", maximumFractionDigits: 0 });
  const cls = (v: number) => (v > 0 ? "pos" : v < 0 ? "neg" : "");
  const dl = new Date(payoff.dates[dateIndex]).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, color: "var(--txt-2)", marginBottom: 6 }}>P&L по ногам при цене {spot.toFixed(2)}</div>
      <table className="bd">
        <thead><tr><th className="l">Нога</th><th>Сегодня</th><th>{dl}</th><th>Экспирация</th><th>Δ к эксп.</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="l"><span className="legname"><span className="sw" style={{ background: r.type === "option" ? (/put|пут/i.test(r.name) ? "var(--put)" : "var(--call)") : "var(--depo)" }} />{r.name}</span></td>
              <td className={cls(r.now)}>{fmt(r.now)}</td>
              <td className={cls(r.at)}>{fmt(r.at)}</td>
              <td className={cls(r.exp)}>{fmt(r.exp)}</td>
              <td className={cls(r.at - r.exp)}>{fmt(r.at - r.exp)}</td>
            </tr>
          ))}
          <tr className="sum"><td className="l">Σ портфеля</td><td className={cls(tN)}>{fmt(tN)}</td><td className={cls(tA)}>{fmt(tA)}</td><td className={cls(tE)}>{fmt(tE)}</td><td className={cls(tA - tE)}>{fmt(tA - tE)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}
